// src/containers/activities/form/ContractorsCard.jsx
//
// Documentation-tab reporting summary for this activity's contractors — sits
// between "Documents & Media" and "Analysis and Reflection". Purely a report-
// oriented rollup + inclusion toggle; contractors themselves are still added
// via the Activity Plan → Inputs step (ContractorInput). Two independent
// parts, each with its own "Show in report" toggle:
//   1. Occupation-category count table — reuses the existing Report Builder
//      /report-builder/charts/contractors endpoint (getInputsContractors),
//      grouped by occupation sub-major group ("occupation category").
//   2. Collapsible contractor-locations map — reuses ContractorMap.jsx as-is
//      (showReportToggle=false suppresses its own Report-Builder-specific
//      toolbar, which doesn't belong in this simpler summary widget).

import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from 'react-i18next';
import { Segment } from 'semantic-ui-react';
import { chartApiGet } from '../../../lib/chartApi';
import ContractorMap from '../../../components/charts/ContractorMap';
import { CustomTable } from '../../../components';
import { IncludeInReportToggle, saveReportInclusion } from './reportFields';
import '../../reportBuilder/ReportBuilder.scss';

const OCCUPATION_TABLE_COLUMNS = [
  { title: 'Count', width: 1, render: (item) => item.count },
  { title: 'Occupation category', render: (item) => item.categoryLabel },
  { title: 'Service type', render: (item) => item.serviceType },
];

// Flattens the category → occupations tree returned by /charts/contractors
// into one row per specific occupation ("Service type"), each still carrying
// its parent category label — the count shown is per occupation, not the
// category total.
const flattenOccupationRows = (categories) =>
  categories.flatMap((cat) =>
    (cat.occupations?.length
      ? cat.occupations
      : [{ occupationCode: null, label: cat.label, count: cat.count }]
    ).map((occ) => ({
      key: `${cat.groupCode ?? 'x'}-${occ.occupationCode ?? 'x'}`,
      count: occ.count,
      categoryLabel: cat.label,
      serviceType: occ.label,
    }))
  );

const parseInclusions = (raw) => {
  try {
    return JSON.parse(raw || '{}');
  } catch {
    return {};
  }
};

let ContractorsCard = ({
  t,
  currentActivity,
  pageDisabled,
  onHasContractors,
}) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [inclusions, setInclusions] = useState(() =>
    parseInclusions(currentActivity.reportInclusions)
  );

  useEffect(() => {
    setInclusions(parseInclusions(currentActivity.reportInclusions));
  }, [currentActivity.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentActivity?.id) return;
    setLoading(true);
    setError(null);
    chartApiGet('contractors', { activityIds: currentActivity.id })
      .then((d) => {
        const cats = d?.categories || [];
        setCategories(cats);
        onHasContractors?.(cats.some((c) => (c.count || 0) > 0));
      })
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  }, [currentActivity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInclusionToggle = useCallback(
    async (key, value) => {
      setInclusions((prev) => ({ ...prev, [key]: value }));
      const merged = await saveReportInclusion({
        activityId: currentActivity.id,
        key,
        value,
      });
      if (merged) {
        setInclusions(merged);
      }
    },
    [currentActivity.id]
  );

  const totalContractors = categories.reduce((s, c) => s + (c.count || 0), 0);

  return (
    <Segment className="contractors-card-seg" disabled={pageDisabled}>
      <div className="contractors-card-body">
        {!loading && !error && totalContractors === 0 && (
          <div className="pm-empty">{t('contractors.none_assigned_yet')}</div>
        )}

        {totalContractors > 0 && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <div style={{ fontWeight: 600 }}>
                {t('contractors.by_occupation')}
              </div>
              <IncludeInReportToggle
                included={inclusions.contractorsTable !== false}
                onToggle={(v) => handleInclusionToggle('contractorsTable', v)}
                disabled={pageDisabled}
              />
            </div>
            <div className="scroll-table" style={{ marginBottom: 16 }}>
              <CustomTable
                header
                columns={OCCUPATION_TABLE_COLUMNS}
                data={flattenOccupationRows(categories)}
                customClass="program-activities-table"
                emptyMessage={t('contractors.no_contractors_added')}
                keyExtractor={(item, i) => item.key ?? i}
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
              onClick={() => setMapOpen((o) => !o)}
            >
              <div style={{ fontWeight: 600 }}>
                <span style={{ marginRight: 6 }}>{mapOpen ? '▾' : '▸'}</span>
                {t('contractors.contractor_locations')}
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <IncludeInReportToggle
                  included={inclusions.contractorsMap !== false}
                  onToggle={(v) => handleInclusionToggle('contractorsMap', v)}
                  disabled={pageDisabled}
                />
              </div>
            </div>
            {mapOpen && (
              <div style={{ marginTop: 8 }}>
                <ContractorMap
                  activityIds={[currentActivity.id]}
                  showReportToggle={false}
                />
              </div>
            )}
          </>
        )}

        {error && (
          <div className="pm-error">
            {t('common.error')}: {error}
          </div>
        )}
      </div>
    </Segment>
  );
};

ContractorsCard = withTranslation()(ContractorsCard);

export default ContractorsCard;
